import { logger } from '@modules/logger';
import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import type { GetUserProfileDetailsQuery } from './user-profile-details.graphql';

export const workspaceTypeValidator = z.union([
  z.literal('INDIVIDUAL'),
  z.literal('TEAM'),
  z.literal('AFFILIATED'),
]);

export type WorkspaceType = z.infer<typeof workspaceTypeValidator>;

export const workspaceOnboardingStatusValidator = z.union([
  z.literal('NOT_STARTED'),
  z.literal('IN_PROGRESS'),
  z.literal('COMPLETED'),
]);

export const workspaceProfileRoleValidator = z.union([
  z.literal('OWNER'),
  z.literal('ADMIN'),
  z.literal('MEMBER'),
]);

export type WorkspaceRole = z.infer<typeof workspaceProfileRoleValidator>;

export const workspacePlanTypeValidator = z.union([
  z.literal('TEAM'),
  z.literal('PREMIUM'),
  z.literal('PRO'),
  z.literal('FREE'),
]);

export type WorkspacePlanType = z.infer<typeof workspacePlanTypeValidator>;

const DEFAULT_MAX_PERSONAL_CARD_LIMIT_FREE_PLAN = 2;

const workspacePlanValidator = z.object({
  is_trialing: z.boolean(),
  type: workspacePlanTypeValidator,
  name: z.string(),
  max_personal_cards: z.number().default(DEFAULT_MAX_PERSONAL_CARD_LIMIT_FREE_PLAN),
});

export const workspaceTagsValidator = z
  .object({
    can_export_contacts: z.boolean().nullable(),
    disable_email_signatures: z.boolean().nullable(),
    enterprise_integrations_enabled: z.boolean().nullable(),
  })
  .nullable();

const workspaceProfileValidator = z.object({
  id: z.string(),
  given_name: z.string().nullable(),
  family_name: z.string().nullable(),
  organization: z.string().nullable(),
  avatar_url: z.string().nullable(),
  role: workspaceProfileRoleValidator,
});

const currentWorkspaceValidator = z.object({
  id: z.string(),
  name: z.string().nullable(),
  type: workspaceTypeValidator,
  plan: workspacePlanValidator,
  onboarding_status: workspaceOnboardingStatusValidator,
  stripe_customer_id: z.string().nullable(),
  avatar_url: z.string().nullable(),
  profile: workspaceProfileValidator,
  tags: workspaceTagsValidator,
});

const userDetailsValidator = z.object({
  id: z.string(),
  email: z.string().nullable(),
  current_workspace: currentWorkspaceValidator,
  individual_stripe_customer_id: z.string().nullable().default(null),
});

type ValidatedUserDetails = z.infer<typeof userDetailsValidator>;

// This ensures the schema is defined correctly and in sync with types generated by GraphQL Codegen
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _: GetUserProfileDetailsQuery['user_details'] = {} as ValidatedUserDetails;

export const transformUserDetailsToCamelCase = (ud: ValidatedUserDetails) =>
  camelcaseKeys(ud, {
    deep: true,
  });

export type UserDetails = ReturnType<typeof transformUserDetailsToCamelCase>;

export const validateApolloResponse = (
  data: GetUserProfileDetailsQuery['user_details'],
): UserDetails => {
  const parsedResponse = userDetailsValidator.safeParse(data);

  if (!parsedResponse.success) {
    // Log issues for observability
    logger.error('Failed to parse user profile details', {
      module: 'user-profile',
      issues: parsedResponse.error.issues,
    });
    const errors = parsedResponse.error.format();

    if (process.env.NODE_ENV === 'development') {
      throw new Error(JSON.stringify(errors));
    }
    // Try our best with the data when in production
    return transformUserDetailsToCamelCase(data as ValidatedUserDetails);
  } else {
    return transformUserDetailsToCamelCase(parsedResponse.data);
  }
};
